import { useEffect, useState } from "react";
import { api, fmtDate, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, GripVertical, Plus, ImagePlus, Pencil, Building2, ExternalLink } from "lucide-react";

export function AdminEvents() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = (orgFilter = selectedOrgFilter) => {
    const q = orgFilter && orgFilter !== "all" ? `?organizer_slug=${orgFilter}` : "";
    api.get(`/admin/events${q}`).then((r) => setEvents(r.data));
  };

  useEffect(() => {
    load();
    api.get("/venues").then((r) => setVenues(r.data));
    if (isSuperAdmin) {
      api.get("/admin/organizers").then((r) => setOrganizers(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const handleQuickAllot = async (eventId, newSlug) => {
    try {
      await api.put(`/admin/events/${eventId}/allot`, { organizer_slug: newSlug });
      const targetOrg = organizers.find((o) => o.slug === newSlug);
      const orgName = targetOrg ? targetOrg.name : (newSlug === "cgc" ? "Common Ground (CGC)" : newSlug);
      toast.success(`Event allotted to ${orgName}. Attendee bookings synchronized!`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not allot event");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Events</h1>
          <p className="text-stone-500 text-sm mt-0.5">
            {isSuperAdmin
              ? "Manage and allot events across platform partners and Common Ground Collective."
              : "Events managed by your organization."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {isSuperAdmin && (
            <div className="flex items-center gap-1.5 bg-card border border-border px-2.5 py-1.5 rounded-md text-xs shadow-sm">
              <Building2 size={13} className="text-orange-600" />
              <span className="font-medium text-stone-500">Partner Filter:</span>
              <select
                className="bg-transparent font-medium border-0 focus:ring-0 text-stone-900 dark:text-stone-100 cursor-pointer"
                value={selectedOrgFilter}
                onChange={(e) => {
                  setSelectedOrgFilter(e.target.value);
                  load(e.target.value);
                }}
              >
                <option value="all">All Organizers & CGC</option>
                <option value="cgc">Common Ground Collective (Direct)</option>
                {organizers.map((o) => (
                  <option key={o.id || o.slug} value={o.slug}>
                    {o.name} ({o.slug})
                  </option>
                ))}
              </select>
            </div>
          )}

          <Button className="btn-brand" onClick={() => setEditing({})} data-testid="new-event-btn">
            <Plus size={14} className="mr-1" /> New event
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {events.map((e) => {
          const isPartnerEvent = e.organizer_slug && e.organizer_slug !== "cgc";
          return (
            <div key={e.id} className="cgc-card p-5 flex flex-col justify-between gap-3" data-testid={`admin-event-${e.slug}`}>
              <div className="space-y-2">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow">{e.category} · {e.status}</div>
                    <div className="font-display text-xl font-semibold mt-1 truncate">{e.title}</div>
                    <div className="text-xs text-stone-500 mt-1">{fmtDate(e.start_at)} · {e.venue_name || e.city}</div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-mono">{e.tiers?.reduce((s, t) => s + (t.sold || 0), 0)} sold</div>
                    <div className="text-xs text-stone-500">of {e.tiers?.reduce((s, t) => s + (t.quantity || 0), 0)}</div>
                  </div>
                </div>

                {/* Partner Allotment Pill */}
                <div className="flex items-center gap-2 pt-1">
                  <div className="flex items-center gap-1.5 text-xs">
                    <Building2 size={13} className={isPartnerEvent ? "text-orange-600" : "text-stone-400"} />
                    <span className={`font-semibold ${isPartnerEvent ? "text-orange-800 dark:text-orange-300" : "text-stone-600 dark:text-stone-400"}`}>
                      {e.organizer || (isPartnerEvent ? e.organizer_slug : "Common Ground (CGC)")}
                    </span>
                  </div>
                  {isPartnerEvent ? (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-orange-100 text-orange-800 dark:bg-orange-950/70 dark:text-orange-300 border border-orange-200">
                      Partner: {e.organizer_slug}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400">
                      CGC Platform
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 text-xs pt-1">
                  {e.tiers?.map((t) => (
                    <span key={t.id} className="px-2 py-0.5 bg-secondary rounded">
                      {t.name} · {t.price_paise === 0 ? "Free" : fmtINR(t.price_paise)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card Footer Actions */}
              <div className="pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2">
                {isSuperAdmin ? (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-[11px] text-stone-500 font-medium">Allot:</span>
                    <select
                      className="text-xs border border-border rounded px-2 py-1 bg-background font-medium focus:ring-1 focus:ring-orange-500"
                      value={e.organizer_slug || "cgc"}
                      onChange={(ev) => handleQuickAllot(e.id, ev.target.value)}
                    >
                      <option value="cgc">Common Ground (CGC Direct)</option>
                      {organizers.map((o) => (
                        <option key={o.id || o.slug} value={o.slug}>
                          {o.name} ({o.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(e)} data-testid={`edit-event-${e.slug}`}>
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  {e.featured && <span className="text-xs px-2 py-1 bg-amber-100 text-amber-900 rounded font-medium">Featured</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing !== null && (
        <EventEditor
          initial={editing}
          venues={venues}
          organizers={organizers}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function EventEditor({ initial, venues, organizers = [], isSuperAdmin, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [f, setF] = useState(() => ({
    id: initial.id,
    title: initial.title || "",
    slug: initial.slug || "",
    organizer_slug: initial.organizer_slug || "cgc",
    organizer: initial.organizer || "Common Ground Collective India",
    subtitle: initial.subtitle || "",
    description: initial.description || "",
    cover_image: initial.cover_image || "",
    category: initial.category || "community",
    status: initial.status || "published",
    featured: !!initial.featured,
    checkout_charge_enabled: initial.checkout_charge_enabled !== false,
    venue_id: initial.venue_id || "",
    city: initial.city || "Visakhapatnam",
    venue_name: initial.venue_name || "",
    venue_address: initial.venue_address || "",
    start_at: toLocal(initial.start_at),
    end_at: toLocal(initial.end_at),
    accessibility: initial.accessibility || "",
    refund_terms: initial.refund_terms || "",
    tags: (initial.tags || []).join(", "),
    photos: initial.photos || [],
    agenda: initial.agenda || [],
    inclusions: initial.inclusions || [],
    faqs: initial.faqs || [],
    tiers: (initial.tiers && initial.tiers.length ? initial.tiers : [{ name: "General", price_paise: 0, quantity: 50, per_order_limit: 10, description: "" }]).map((t) => ({
      ...t,
      price_rupees: t.price_rupees !== undefined ? t.price_rupees : ((t.price_paise || 0) / 100),
    })),
    custom_questions: initial.custom_questions || [],
  }));

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [newPhoto, setNewPhoto] = useState("");
  const [newInclusion, setNewInclusion] = useState("");

  const save = async () => {
    if (!f.title || !f.start_at || !f.end_at) return toast.error("Title, start, and end are required");
    if (!f.tiers.length) return toast.error("At least one ticket tier");
    const payload = {
      title: f.title,
      slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
      organizer_slug: f.organizer_slug || "cgc",
      subtitle: f.subtitle,
      description: f.description,
      cover_image: f.cover_image,
      category: f.category,
      status: f.status,
      featured: f.featured,
      checkout_charge_enabled: f.checkout_charge_enabled,
      venue_id: f.venue_id || null,
      city: f.city,
      venue_name: f.venue_name,
      venue_address: f.venue_address,
      start_at: new Date(f.start_at).toISOString(),
      end_at: new Date(f.end_at).toISOString(),
      accessibility: f.accessibility,
      refund_terms: f.refund_terms,
      tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
      photos: f.photos,
      agenda: f.agenda,
      inclusions: f.inclusions,
      faqs: f.faqs,
      tiers: f.tiers.map((t) => {
        const r = t.price_rupees !== undefined ? parseFloat(t.price_rupees) : ((t.price_paise || 0) / 100);
        return {
          ...t,
          price_paise: Math.round((isNaN(r) ? 0 : r) * 100),
          quantity: parseInt(t.quantity) || 0,
          per_order_limit: parseInt(t.per_order_limit) || 10,
        };
      }),
      custom_questions: f.custom_questions.filter((q) => q.label).map((q) => ({
        key: q.key || `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label: q.label, type: q.type, required: !!q.required,
        options: q.type === "select" ? (q.optionsText || (q.options || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean) : [],
      })),
    };
    try {
      if (isNew) await api.post("/admin/events", payload);
      else await api.put(`/admin/events/${f.id}`, payload);
      toast.success(isNew ? "Event created" : "Event updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "Create event" : `Edit · ${initial.title}`}</DialogTitle></DialogHeader>

        <Section title="Basics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isSuperAdmin && (
              <div className="md:col-span-2 p-3.5 rounded-lg border border-orange-200 bg-orange-50/50 dark:bg-orange-950/20 dark:border-orange-900/60 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold flex items-center gap-1.5 text-stone-900 dark:text-stone-100">
                    <Building2 size={15} className="text-orange-600" />
                    Partner / Organizer Allotment
                  </Label>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 font-semibold">
                    Current: {f.organizer_slug || "cgc"}
                  </span>
                </div>
                <select
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background font-medium focus:ring-2 focus:ring-orange-500"
                  value={f.organizer_slug || "cgc"}
                  onChange={(e) => {
                    const slug = e.target.value;
                    const org = (organizers || []).find((o) => o.slug === slug);
                    setF({
                      ...f,
                      organizer_slug: slug,
                      organizer: org ? org.name : (slug === "cgc" ? "Common Ground Collective India" : slug),
                    });
                  }}
                  data-testid="ee-organizer-slug"
                >
                  <option value="cgc">Common Ground Collective (CGC Direct / Platform Default)</option>
                  {organizers.filter((o) => o.slug !== "cgc").map((o) => (
                    <option key={o.id || o.slug} value={o.slug}>
                      {o.name} ({o.slug})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-stone-500">
                  Allot this event to a collaborating partner (e.g. Offclub). The partner will see this event and all attendee bookings in their dedicated partner admin dashboard.
                </p>
              </div>
            )}
            <F label="Title *" testid="ee-title" value={f.title} onChange={(v) => upd("title", v)} />
            <F label="Slug (auto if empty)" testid="ee-slug" value={f.slug} onChange={(v) => upd("slug", v)} />
            <F label="Subtitle" testid="ee-subtitle" value={f.subtitle} onChange={(v) => upd("subtitle", v)} />
            <F label="Cover image URL" testid="ee-cover" value={f.cover_image} onChange={(v) => upd("cover_image", v)} />
            <div>
              <Label className="text-xs">Category</Label>
              <select className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background" value={f.category} onChange={(e) => upd("category", e.target.value)} data-testid="ee-category">
                {["fundraiser","workshop","popup","community","cultural"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background" value={f.status} onChange={(e) => upd("status", e.target.value)} data-testid="ee-status">
                {["draft","published","cancelled","archived"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={f.featured} onChange={(e) => upd("featured", e.target.checked)} data-testid="ee-featured" />Featured on homepage</label>
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={f.checkout_charge_enabled} onChange={(e) => upd("checkout_charge_enabled", e.target.checked)} data-testid="ee-charge" />Apply platform checkout charge</label>
          </div>
          <div className="mt-3"><Label className="text-xs">Description</Label><Textarea rows={4} value={f.description} onChange={(e) => upd("description", e.target.value)} data-testid="ee-description" /></div>
        </Section>

        <Section title="When & where">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Start *" testid="ee-start" value={f.start_at} onChange={(v) => upd("start_at", v)} type="datetime-local" />
            <F label="End *" testid="ee-end" value={f.end_at} onChange={(v) => upd("end_at", v)} type="datetime-local" />
            <div className="md:col-span-2">
              <Label className="text-xs">Venue</Label>
              <select className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background" value={f.venue_id} onChange={(e) => {
                const v = (venues || []).find((x) => x.id === e.target.value);
                if (v) setF({ ...f, venue_id: v.id, venue_name: v.name, venue_address: v.address || "", city: v.city, photos: f.photos.length ? f.photos : (v.photos || []) });
                else setF({ ...f, venue_id: "" });
              }} data-testid="ee-venue">
                <option value="">— Manual / online —</option>
                {(venues || []).map((v) => <option key={v.id} value={v.id}>{v.name} · {v.city}</option>)}
              </select>
            </div>
            <F label="City" testid="ee-city" value={f.city} onChange={(v) => upd("city", v)} />
            <F label="Venue name" testid="ee-vname" value={f.venue_name} onChange={(v) => upd("venue_name", v)} />
            <div className="md:col-span-2"><F label="Venue address" testid="ee-vaddr" value={f.venue_address} onChange={(v) => upd("venue_address", v)} /></div>
          </div>
        </Section>

        <Section title="Ticket tiers">
          {f.tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mt-2 pt-2 border-t border-border first:border-0 first:pt-0">
              <div className="col-span-3"><Label className="text-xs">Name</Label><Input value={t.name} onChange={(e) => setTier(i, { name: e.target.value })} data-testid={`ee-tier-name-${i}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Price (₹)</Label><Input type="number" min="0" step="any" placeholder="0" value={t.price_rupees !== undefined ? t.price_rupees : ((t.price_paise || 0) / 100)} onChange={(e) => setTier(i, { price_rupees: e.target.value, price_paise: Math.round((parseFloat(e.target.value) || 0) * 100) })} data-testid={`ee-tier-price-${i}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Qty</Label><Input type="number" value={t.quantity} onChange={(e) => setTier(i, { quantity: e.target.value })} data-testid={`ee-tier-qty-${i}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Per-order</Label><Input type="number" value={t.per_order_limit || 10} onChange={(e) => setTier(i, { per_order_limit: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={t.description || ""} onChange={(e) => setTier(i, { description: e.target.value })} /></div>
              <div className="col-span-1"><Button size="sm" variant="outline" onClick={() => rmTier(i)} data-testid={`ee-tier-rm-${i}`}><Trash2 size={12} /></Button></div>
              {t.sold > 0 && <div className="col-span-12 text-[11px] text-stone-500">Sold: {t.sold} · Reserved: {t.reserved || 0}. These cannot be reduced below sold count.</div>}
            </div>
          ))}
          <Button size="sm" variant="outline" className="mt-3" onClick={addTier} data-testid="ee-tier-add"><Plus size={12} className="mr-1"/>Add tier</Button>
        </Section>

        <Section title="Agenda">
          {f.agenda.map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mt-2">
              <div className="col-span-2"><Label className="text-xs">Time</Label><Input value={a.time || ""} onChange={(e) => setAgenda(i, { time: e.target.value })} placeholder="10:00" data-testid={`ee-ag-time-${i}`} /></div>
              <div className="col-span-4"><Label className="text-xs">Title</Label><Input value={a.title || ""} onChange={(e) => setAgenda(i, { title: e.target.value })} data-testid={`ee-ag-title-${i}`} /></div>
              <div className="col-span-5"><Label className="text-xs">Description</Label><Input value={a.description || ""} onChange={(e) => setAgenda(i, { description: e.target.value })} /></div>
              <div className="col-span-1"><Button size="sm" variant="outline" onClick={() => setF({ ...f, agenda: f.agenda.filter((_, x) => x !== i) })}><Trash2 size={12} /></Button></div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setF({ ...f, agenda: [...f.agenda, { time: "", title: "", description: "" }] })} data-testid="ee-ag-add"><Plus size={12} className="mr-1"/>Add slot</Button>
        </Section>

        <Section title="Inclusions">
          <div className="flex gap-2">
            <Input placeholder="Add an inclusion e.g. Lunch" value={newInclusion} onChange={(e) => setNewInclusion(e.target.value)} data-testid="ee-inc-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newInclusion) { upd("inclusions", [...f.inclusions, newInclusion]); setNewInclusion(""); } } }} />
            <Button size="sm" variant="outline" onClick={() => { if (newInclusion) { upd("inclusions", [...f.inclusions, newInclusion]); setNewInclusion(""); } }} data-testid="ee-inc-add"><Plus size={12}/></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {f.inclusions.map((it, i) => (
              <span key={i} className="text-xs bg-secondary rounded px-2 py-1 flex items-center gap-1">{it}<button onClick={() => upd("inclusions", f.inclusions.filter((_, x) => x !== i))} className="ml-1"><Trash2 size={10} /></button></span>
            ))}
          </div>
        </Section>

        <Section title="FAQs">
          {f.faqs.map((q, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mt-2">
              <div className="col-span-4"><Label className="text-xs">Question</Label><Input value={q.q || ""} onChange={(e) => setFaq(i, { q: e.target.value })} data-testid={`ee-faq-q-${i}`} /></div>
              <div className="col-span-7"><Label className="text-xs">Answer</Label><Input value={q.a || ""} onChange={(e) => setFaq(i, { a: e.target.value })} data-testid={`ee-faq-a-${i}`} /></div>
              <div className="col-span-1"><Button size="sm" variant="outline" onClick={() => setF({ ...f, faqs: f.faqs.filter((_, x) => x !== i) })}><Trash2 size={12} /></Button></div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setF({ ...f, faqs: [...f.faqs, { q: "", a: "" }] })} data-testid="ee-faq-add"><Plus size={12} className="mr-1"/>Add FAQ</Button>
        </Section>

        <Section title="Photo gallery (carousel)">
          <p className="text-xs text-stone-500">Paste image URLs. These appear as a gallery on the event page.</p>
          <div className="flex gap-2 mt-3">
            <Input placeholder="https://images.example.com/photo.jpg" value={newPhoto} onChange={(e) => setNewPhoto(e.target.value)} data-testid="ee-photo-input" />
            <Button size="sm" variant="outline" onClick={() => { if (newPhoto) { upd("photos", [...f.photos, newPhoto]); setNewPhoto(""); } }} data-testid="ee-photo-add"><ImagePlus size={12}/></Button>
          </div>
          {f.photos.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3">
              {f.photos.map((p, i) => (
                <div key={i} className="relative group" data-testid={`ee-photo-${i}`}>
                  <img src={p} alt="" className="w-full h-20 object-cover rounded-md border border-border" />
                  <button className="absolute top-1 right-1 bg-red-600 text-white rounded p-1 opacity-90" onClick={() => upd("photos", f.photos.filter((_, x) => x !== i))}><Trash2 size={10}/></button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Custom questions">
          <p className="text-xs text-stone-500">Ask each attendee for age, diet, affiliation etc.</p>
          {f.custom_questions.map((q, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mt-2">
              <div className="col-span-4"><Label className="text-xs">Label</Label><Input value={q.label} onChange={(e) => setCq(i, { label: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Type</Label>
                <select className="mt-1 w-full border border-border rounded-md px-2 py-2 text-sm bg-background" value={q.type} onChange={(e) => setCq(i, { type: e.target.value })}>
                  <option value="text">Text</option><option value="select">Dropdown</option><option value="phone">Phone</option><option value="number">Number</option>
                </select>
              </div>
              <div className="col-span-4"><Label className="text-xs">Options (comma) if dropdown</Label><Input value={q.optionsText || (q.options || []).join(", ")} onChange={(e) => setCq(i, { optionsText: e.target.value })} disabled={q.type !== "select"} /></div>
              <div className="col-span-1 flex items-center gap-1"><input type="checkbox" checked={q.required} onChange={(e) => setCq(i, { required: e.target.checked })} /><span className="text-xs">Req</span></div>
              <div className="col-span-1"><Button size="sm" variant="outline" onClick={() => setF({ ...f, custom_questions: f.custom_questions.filter((_, x) => x !== i) })}><Trash2 size={12}/></Button></div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setF({ ...f, custom_questions: [...f.custom_questions, { key: `q_${Date.now()}`, label: "", type: "text", required: false, options: [] }] })} data-testid="ee-cq-add"><Plus size={12} className="mr-1"/>Add question</Button>
        </Section>

        <Section title="Extras">
          <F label="Tags (comma separated)" testid="ee-tags" value={f.tags} onChange={(v) => upd("tags", v)} />
          <div className="mt-3"><Label className="text-xs">Accessibility notes</Label><Textarea rows={2} value={f.accessibility} onChange={(e) => upd("accessibility", e.target.value)} data-testid="ee-access" /></div>
          <div className="mt-3"><Label className="text-xs">Refund terms</Label><Textarea rows={2} value={f.refund_terms} onChange={(e) => upd("refund_terms", e.target.value)} data-testid="ee-refund" /></div>
        </Section>

        <div className="flex gap-2 justify-end sticky bottom-0 bg-background pt-3 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="btn-brand" onClick={save} data-testid="ee-save">{isNew ? "Create event" : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  function setTier(i, patch) { const n=[...f.tiers]; n[i] = { ...n[i], ...patch }; upd("tiers", n); }
  function addTier() { upd("tiers", [...f.tiers, { name: "New tier", price_rupees: 0, price_paise: 0, quantity: 50, per_order_limit: 10, description: "" }]); }
  function rmTier(i) { upd("tiers", f.tiers.filter((_, x) => x !== i)); }
  function setAgenda(i, patch) { const n=[...f.agenda]; n[i]={...n[i],...patch}; upd("agenda", n); }
  function setFaq(i, patch) { const n=[...f.faqs]; n[i]={...n[i],...patch}; upd("faqs", n); }
  function setCq(i, patch) { const n=[...f.custom_questions]; n[i]={...n[i],...patch}; upd("custom_questions", n); }
}

function toLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function Section({ title, children }) {
  return (
    <div className="border-t border-border pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
      <div className="eyebrow mb-3">{title}</div>
      {children}
    </div>
  );
}
function F({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}
